import React, { useState, useEffect, useMemo } from 'react'

import { Card } from 'components'
import { useModal, usePersistForm, useAuth } from 'hooks'
import useLoading from 'hooks/useLoading'
import moment, { Moment } from 'moment'
import {
  ChecklistServices,
  IChecklistFilterResponse,
  ISearchFilter
} from 'services/NGT/PendingAnalysis'
import { MovDocumentItemServices } from 'services/Supplier/MovDocumentItem'
import { SPACING } from 'utils'

import { FilterChecklistPending, TableChecklistPending } from './components'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

export interface SearchFilterProps {
  comDocumento?: string
  CdContrato: number | string
  idRolDocumento?: number
  NmFornecedor: string
  IdTipoPendencia?: number
  DtCompetenciaIni: string
  DtCompetenciaFim: string
  DtAnexadoIni: string
  DtAnexadoFim: string
}

const DEFAULT_SEARCH_FILTER: SearchFilterProps = {
  CdContrato: '',
  DtAnexadoFim: null,
  DtAnexadoIni: null,
  DtCompetenciaFim: null,
  DtCompetenciaIni: null,
  IdTipoPendencia: null,
  NmFornecedor: '',
  // PageNumber: 1,
  // PageSize: 15,
  // eslint-disable-next-line sort-keys
  comDocumento: null
}

const SCHEMA = yup.object({
  CdContrato: yup.string().nullable().notRequired(),
  DtAnexadoFim: yup
    .date()
    .typeError('Digite uma data válida.')
    .nullable()
    .notRequired()
    .min(
      yup.ref('DtAnexadoIni'),
      'Data anexado fim deve ser maior que data anexada inicio.'
    ),
  DtAnexadoIni: yup
    .date()
    .typeError('Digite uma data válida.')
    .nullable()
    .notRequired(),
  DtCompetenciaFim: yup
    .date()
    .typeError('Digite uma data válida.')
    .nullable()
    .notRequired()
    .min(
      yup.ref('DtCompetenciaIni'),
      'Data competência fim deve ser maior que data competência inicio.'
    ),
  DtCompetenciaIni: yup
    .date()
    .typeError('Digite uma data válida.')
    .nullable()
    .notRequired(),
  IdTipoPendencia: yup.number().nullable().notRequired(),
  NmFornecedor: yup.string().nullable().notRequired(),
  comDocumento: yup.string().nullable().notRequired()
})

const CheckListPending = () => {
  const { showModal } = useModal()
  const { setLoading } = useLoading()
  const {
    control,
    reset,
    formState: { errors },
    setValue,
    watch,
    handleSubmit
  } = useForm<SearchFilterProps>({
    resolver: yupResolver(SCHEMA),
    defaultValues: {
      CdContrato: null,
      DtAnexadoFim: null,
      DtAnexadoIni: null,
      DtCompetenciaFim: null,
      DtCompetenciaIni: null,
      comDocumento: 'T'
    }
  })

  usePersistForm('ngt_checklist_pending_search', {
    setValue,
    watch
  })

  const { user } = useAuth()
  const idTipoPendencia = watch('IdTipoPendencia')

  const [rowsPerPage, setRowsPerPage] = useState<number>(15)
  const [page, setPage] = useState<number>(1)
  const [totalItems, setTotalItems] = useState<number>(0)
  const [tableData, setTableData] = useState<IChecklistFilterResponse[]>([])
  const [changeEffect, setChangeEffect] = useState<boolean>(false) // used for effect changes

  const DtCompetenciaIni = watch('DtCompetenciaIni')
  const DtCompetenciaFim = watch('DtCompetenciaFim')
  const DtAnexadoIni = watch('DtAnexadoIni')
  const DtAnexadoFim = watch('DtAnexadoFim')

  const areDatesValid = useMemo(() => {
    if (
      (moment(DtCompetenciaIni).isValid() &&
        moment(DtCompetenciaFim).isValid() &&
        moment(DtCompetenciaIni).diff(moment(DtCompetenciaFim)) > 0) ||
      (moment(DtCompetenciaIni).isValid() &&
        !moment(DtCompetenciaFim).isValid()) ||
      (!moment(DtCompetenciaIni).isValid() &&
        moment(DtCompetenciaFim).isValid()) ||
      (!moment(DtAnexadoIni).isValid() && moment(DtAnexadoFim).isValid())
    ) {
      return false
    }

    return (
      moment(DtAnexadoFim || moment()).isValid() &&
      moment(DtAnexadoIni || moment()).isValid() &&
      moment(DtCompetenciaFim || moment()).isValid() &&
      moment(DtCompetenciaIni || moment()).isValid() &&
      DtAnexadoIni <= DtAnexadoFim
    )
  }, [DtAnexadoFim, DtAnexadoIni, DtCompetenciaFim, DtCompetenciaIni])

  const handleBlockItem = (idMovDocumentoItem: number) => {
    setLoading('loading')
    MovDocumentItemServices()
      .UpdateStatusMovDocumentItem(idMovDocumentoItem, 'Recusado', user.IdUsuario)
      .then(() => {
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: 'Recusado com sucesso.',
          type: 'success'
        })
        setChangeEffect((oldState) => !oldState)
      })
      .catch(() => {
        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: 'Infelizmente não foi possível bloquear.',
          type: 'error'
        })
      })
  }

  const handleOnClear = () => {
    reset()
    setChangeEffect((oldState) => !oldState)
  }

  const handleOnSearch = (
    searchFilterData: SearchFilterProps = {} as SearchFilterProps
  ) => {
    if (page == 1) {
      handleSearchFilterItens(searchFilterData)
      return
    }
    setPage(1)
  }

  const handleSearchFilterItens = (
    searchFilterData: SearchFilterProps = {} as SearchFilterProps
  ) => {
    setLoading('loading')
    ChecklistServices()
      .getFilter({
        ...(searchFilterData.CdContrato && {
          CdContrato: searchFilterData.CdContrato
        }),
        ...(searchFilterData.NmFornecedor && {
          NmFornecedor: searchFilterData.NmFornecedor
        }),
        ...(searchFilterData.IdTipoPendencia && {
          IdTipoPendencia: searchFilterData.IdTipoPendencia
        }),
        ...(searchFilterData.idRolDocumento && {
          idRolDocumento: searchFilterData.idRolDocumento
        }),
        ...(searchFilterData.comDocumento != 'T' && {
          comDocumento: searchFilterData.comDocumento === 'true' ? true : false
        }),
        ...(searchFilterData.DtAnexadoFim && {
          DtAnexadoFim: moment(searchFilterData.DtAnexadoFim).isValid()
            ? moment(searchFilterData.DtAnexadoFim).toDate()
            : null
        }),
        ...(searchFilterData.DtAnexadoIni && {
          DtAnexadoIni: moment(searchFilterData.DtAnexadoIni).isValid()
            ? moment(searchFilterData.DtAnexadoIni).toDate()
            : null
        }),
        ...(searchFilterData.DtCompetenciaFim && {
          DtCompetenciaFim: moment(searchFilterData.DtCompetenciaFim).isValid()
            ? moment(searchFilterData.DtCompetenciaFim).toDate()
            : null
        }),
        ...(searchFilterData.DtCompetenciaIni && {
          DtCompetenciaIni: moment(searchFilterData.DtCompetenciaIni).isValid()
            ? moment(searchFilterData.DtCompetenciaIni).toDate()
            : null
        }),
        PageNumber: page,
        PageSize: rowsPerPage
      })
      .then(({ data: { entity, sucesso } }) => {
        if (!sucesso) {
          throw new Error()
        }

        setTableData(entity.items)
        setTotalItems(entity.pageCount)
        setLoading('idle')
      })
      .catch(() => {
        setLoading('error')
        showModal({
          hideButtonCancel: true,
          message: 'Não foi possivel obter os dados de pendencia checklist',
          type: 'error'
        })
      })
  }

  useEffect(() => {
    handleSearchFilterItens(watch())
  }, [page, rowsPerPage])

  useEffect(() => {
    if (page === 1) {
      handleSearchFilterItens(watch())
      return
    }
    setPage(1)
  }, [changeEffect])

  return (
    <>
      <Card
        marginLeft={SPACING.spacing20}
        marginRight={SPACING.spacing20}
        marginBottom={SPACING.spacing100}
        padding={SPACING.spacing20}
        boxShadow={`0px -1px 32px rgba(50, 50, 71, 0.12), 0px 24px 32px rgba(50, 50, 71, 0.12)`}
        borderRadius={`0px 10px 0px 0px`}
      >
        <FilterChecklistPending
          areDatesValid={areDatesValid}
          value={idTipoPendencia ?? null}
          handleSubmit={handleSubmit}
          control={control}
          errors={errors}
          handleOnClear={handleOnClear}
          setChangeEffect={setChangeEffect}
          handleOnSearch={handleOnSearch}
        />
        <TableChecklistPending
          areDatesValid={areDatesValid}
          handleBlockItem={handleBlockItem}
          setPage={setPage}
          setChangeEffect={setChangeEffect}
          setRowsPerPage={setRowsPerPage}
          rowsPerPage={rowsPerPage}
          page={page}
          totalItems={totalItems}
          tableData={tableData}
        />
      </Card>
    </>
  )
}

export default CheckListPending
